import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  useTheme,
  alpha,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import SearchIcon from "@mui/icons-material/Search";

import { useDispatch, useSelector } from "react-redux";
import { SIDEBAR_ITEMS } from "../../config/sidebarConfig";
import { tokens } from "../../theme";
import { usePermissions } from "../../hooks/usePermissions";
import { openTab } from "../../redux/actions/workspaceActions";
import { toggleSidebar } from "../../redux/actions/uiActions";
import { castEnumForDisplay } from "../../helpers/enumHelpers";
import { STANDALONE_ROUTES } from "../../config/standAlonePagesRoutes";
import Logo from "../Logo";

const SideBarNavigation = ({ onMobileClose }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfo, hasPermission } = usePermissions();
  const { isSidebarCollapsed } = useSelector((state) => state.ui);
  const { activeTabId } = useSelector((state) => state.workspace);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredItems = useMemo(() => {
    if (!userInfo) return [];

    let items = SIDEBAR_ITEMS.filter((link) => {
      if (link.isHeader) return true;
      if (!link.permission) return true;
      return hasPermission(link.permission);
    });

    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase();
      items = items.filter((item) => {
        if (item.isHeader) return true; // Keep headers, we'll clean them below
        return item.title.toLowerCase().includes(lowerSearch);
      });
    }

    // 3. Clean up orphaned headers (Headers that have no matching children below them)
    return items.filter((item, index, array) => {
      if (!item.isHeader) return true;
      // It is a header. Keep it ONLY if the next item exists AND is not a header.
      const nextItem = array[index + 1];
      return nextItem && !nextItem.isHeader;
    });
  }, [userInfo, hasPermission, searchTerm]);

  const handleNavigation = (item) => {
    dispatch(
      openTab({
        id: item.path,
        title: item.title,
        path: item.path,
        componentKey: item.componentKey,
      }),
    );
    navigate(item.path);

    if (onMobileClose) onMobileClose();
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background:
          theme.palette.mode === "dark"
            ? colors.primary[400]
            : colors.grey[900],
        overflowY: "auto",
        overflowX: "hidden",
        "&::-webkit-scrollbar": {
          width: "4px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
          margin: "4px 0",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: alpha(theme.palette.text.primary, 0.25),
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: alpha(theme.palette.text.primary, 0.45),
        },
      }}
    >
      {/* 1. BRAND HEADER & TOGGLE */}
      <Box
        sx={{
          p: isSidebarCollapsed ? 2 : 3, // Adapts padding based on state
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${colors.grey[800]}`,
        }}
      >
        {!isSidebarCollapsed && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: theme.appRadius?.sm || 1,
                color: "white",
                display: "flex",
              }}
            >
              <Logo size="md" />
            </Box>
            <Typography variant="h5" fontWeight="bold" color="white">
              Fiji Licensing
            </Typography>
          </Box>
        )}

        <IconButton
          onClick={() => dispatch(toggleSidebar())}
          sx={{ color: "white", mx: isSidebarCollapsed ? "auto" : 0 }}
        >
          <MenuOpenIcon
            sx={{
              transform: isSidebarCollapsed ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s",
            }}
          />
        </IconButton>
      </Box>

      {/* 2. USER PROFILE MINI-CARD (Original Styles Restored) */}
      {!isSidebarCollapsed && (
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: theme.appRadius?.md || 2,
              background: colors.gradients?.glass || "rgba(255,255,255,0.05)",
              border: `1px solid ${alpha(colors.grey[500], 0.2)}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <Avatar
              src={userInfo?.photoUrl}
              sx={{
                width: 56,
                height: 56,
                mb: 1.5,
                border: `2px solid ${colors.blue[500]}`,
              }}
            />
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color={colors.grey[100]}
            >
              {userInfo?.firstName} {userInfo?.lastName}
            </Typography>
            <Chip
              label={castEnumForDisplay(userInfo?.userRole || "User")}
              size="small"
              sx={{
                mt: 0.5,
                height: 20,
                fontSize: "0.65rem",
                bgcolor: colors.blue[500],
                color: "white",
                fontWeight: 600,
              }}
            />
          </Box>
        </Box>
      )}

      {/* 3. GLOBAL SEARCH (Styled like FormTextField) */}
      {!isSidebarCollapsed && (
        <Box sx={{ px: 2, pb: 1, pt: 1 }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search modules..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{ color: colors.grey[400], fontSize: "1.2rem" }}
                  />
                </InputAdornment>
              ),
              sx: {
                borderRadius: theme.appRadius?.sm || 1,
                bgcolor: "rgba(255,255,255,0.05)", // Subtle dark-mode friendly bg
                color: colors.grey[100],
                "& fieldset": { borderColor: "transparent" },
                "&:hover fieldset": {
                  borderColor: alpha(colors.grey[100], 0.2),
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.blue[500],
                },
                "& input": { py: 1 }, // Tighter padding
              },
            }}
          />
        </Box>
      )}

      {/* 4. NAVIGATION LINKS */}
      <List sx={{ px: isSidebarCollapsed ? 1 : 2, pb: 4 }}>
        {filteredItems.map((item, index) => {
          // RENDER SECTION HEADER
          if (item.isHeader) {
            if (isSidebarCollapsed) {
              return (
                <Box
                  key={index}
                  sx={{
                    my: 1.5,
                    borderBottom: `1px solid ${colors.grey[800]}`,
                  }}
                />
              );
            }
            return (
              <Typography
                key={index}
                variant="overline"
                sx={{
                  display: "block",
                  mt: 3, // Restored original margin top
                  mb: 1, // Restored original margin bottom
                  px: 2,
                  color: colors.grey[400],
                  fontWeight: 700,
                  opacity: 0.7,
                }}
              >
                {item.title}
              </Typography>
            );
          }

          // RENDER LINK ITEM
          const isStandAlonePage = STANDALONE_ROUTES.some((r) =>
            location.pathname.startsWith(r),
          );

          const isActive = isStandAlonePage
            ? location.pathname.startsWith(item.path)
            : activeTabId === item.path;

          const Icon = item.icon;

          const buttonContent = (
            <ListItemButton
              onClick={() => handleNavigation(item)}
              sx={{
                mb: 0.5,
                borderRadius: theme.appRadius?.md || 1,
                justifyContent: isSidebarCollapsed ? "center" : "flex-start",
                px: isSidebarCollapsed ? 1 : 2,
                bgcolor: isActive
                  ? alpha(colors.blue[500], 0.15)
                  : "transparent",
                borderLeft: isActive
                  ? `4px solid ${colors.blue[500]}`
                  : "4px solid transparent",
                color: isActive ? colors.blue[100] : colors.grey[300],
                "&:hover": {
                  bgcolor: alpha(colors.grey[500], 0.2),
                  color: colors.grey[100],
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: isSidebarCollapsed ? 0 : 36,
                  mr: isSidebarCollapsed ? 0 : "auto",
                  justifyContent: "center",
                  color: isActive ? colors.blue[400] : colors.grey[400],
                }}
              >
                <Icon size={20} />
              </ListItemIcon>
              {!isSidebarCollapsed && (
                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{
                    fontSize: "0.9rem",
                    fontWeight: isActive ? 600 : 400,
                  }}
                />
              )}
            </ListItemButton>
          );

          return isSidebarCollapsed ? (
            <Tooltip key={item.path} title={item.title} placement="right">
              {buttonContent}
            </Tooltip>
          ) : (
            <React.Fragment key={item.path}>{buttonContent}</React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default SideBarNavigation;
